import {
  CapabilityNav,
  PageHeader,
  Table,
  useTreeData,
} from "@velocity-ehs/component-library";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Column } from "primereact/column";
import { ChangeEvent, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useGetUserRoleLocationCount } from "../../../hooks/useGetRoleLocationCount";
import {
  SortDirection,
  defaultPagination,
} from "../../supportFunctions/graphql/Queries";
import { navItems } from "./NavHelpers";
import { isEqual } from "lodash";

const defaultParams = {
  ...defaultPagination,
  rows: 20,
  sortField: "roleName",
  sortOrder: SortDirection.ASC,
  filters: {
    roleName: {
      value: null,
    },
    locationIDs: {
      value: [],
    },
  },
};

const RolesByLocation = () => {
  const history = useHistory();
  const allFlags = useFlags();
  const treeData = useTreeData();
  const { t } = useTranslation();
  const [lazyParams, setLazyParams] = useState(defaultParams);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { userRoleLocationCount, pagination, isFetching } =
    useGetUserRoleLocationCount(lazyParams);

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  const handleGlobalSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setLazyParams((previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          roleName: {
            value: event.target.value,
          },
        },
      })),
    []
  );

  const onLocationsFilter = (locationIds: string[]) => {
    setLazyParams((state: any) => {
      return {
        ...state,
        ...defaultPagination,
        filters: {
          ...state.filters,
          locationIDs: { value: locationIds },
        },
      };
    });
  };

  const handleClearFilters = () => {
    setLazyParams(defaultParams);
  };

  const isDefaultFilter = isEqual(defaultParams.filters, lazyParams.filters);

  return (
    <div className="App">
      <CapabilityNav
        capability="Roles Management"
        navItems={navItems()}
        featureFlags={allFlags}
      />
      <div className="mainContent">
        <>
          <PageHeader
            title={t("universal_assignment_by_location")}
            navigationTree={[
              { text: `Accelerate ${t("universal_Home")}`, href: "/" },
              { text: t("universal_assignment_by_location"), href: "" },
            ]}
          />
          <div className="card__fullContent">
            <Table
              data={userRoleLocationCount}
              params={lazyParams}
              totalRecords={totalRecords}
              selectionMode="single"
              onRowSelect={(rowData: {
                data: { roleID: string; roleName: string; locationID: string };
              }) =>
                history.push(
                  `/role-management/location-roles/${rowData.data.roleID}?roleName=${rowData.data.roleName}&locationID=${rowData.data.locationID}`
                )
              }
              onPage={setLazyParams}
              onSort={setLazyParams}
              isLoading={isFetching || !treeData.isAllFetched}
              onGlobalSearch={handleGlobalSearch}
              showLocationsFilter
              onLocationsFilter={onLocationsFilter}
              clearFilterCallback={handleClearFilters}
              isClearFiltersButtonDisabled={isDefaultFilter}
              testId="roles-by-location-table"
            >
              <Column field="roleName" header={t("universal_Role")} sortable />
              <Column field="locationName" header={t("universal_location")} />
              <Column
                field="roleDescription"
                header={t("universal_Description")}
              />
              <Column
                field="count"
                header={t("universal_number_of_users")}
                sortable
                body={(rowData) => <span>{rowData.count ?? "--"}</span>}
              />
            </Table>
          </div>
        </>
      </div>
    </div>
  );
};

export default RolesByLocation;
